import React, { useState, useEffect, useCallback } from 'react';
import './HelpGuide.css';

// ─────────────────────────────────────────────
// QUICK START STEPS — uses your tutorial screenshots
// Images should be placed in /public/tutorial/ folder
// ─────────────────────────────────────────────
const QUICK_START_STEPS = [
  {
    id: 1,
    title: '👋 Welcome to Distil Protocol',
    image: '/tutorial/image1.png',
  },
  {
    id: 2,
    title: '🛡️ Safety First — Protect Valuables First',
    image: '/tutorial/image2.png',
    caption: 'Before your first purge: open your wallet on DEX Screener, identify anything worth $10+, and whitelist those tokens. Purged tokens are GONE FOREVER — 2 minutes of protection saves real money. When in doubt, whitelist it.',
  },
  {
    id: 3,
    title: '🏆 Your Badge Is Your Flex',
    image: '/tutorial/image5.png',
    caption: 'Start as a Novice and climb to MYTHIC across 8 legendary tiers. Every purge = more points = bigger flex. Watch the glow-up happen.',
  },
  {
    id: 4,
    title: '📊 Your Stats Dashboard',
    image: '/tutorial/image7.png',
    caption: 'Track your total purity points, purifications completed, and objects cleaned. Watch these numbers grow with every flush!',
  },
  {
    id: 5,
    title: '🧹 Wallet Cleanup Section',
    image: '/tutorial/image8.png',
  },
  {
    id: 6,
    title: '👀 What Your Wallet Looks Like',
    image: '/tutorial/image9.png',
    caption: 'Tokens are grouped and sorted by toxicity. Dust tokens, scam airdrops, and zero-value objects are flagged automatically. USD values help you decide what to keep.',
  },
  {
    id: 7,
    title: '🔒 Whitelist Protection',
    image: '/tutorial/image10.png',
  },
  {
    id: 8,
    title: '⚗️ The Distillery',
    image: '/tutorial/image11.png',
    caption: 'Watch your selected objects flow through the purification process! Toxic waste transforms into pure $DISTIL tokens. 92% goes to you, 8% to the protocol. That\'s the split.',
  },
  {
    id: 9,
    title: '🚽👑 Toilet Mode (Premium)',
    image: '/tutorial/image12.png',
  },
  {
    id: 10,
    title: '🎮 Practice Makes Perfect',
    image: '/tutorial/image13.png',
  },
  {
    id: 11,
    title: '🚀 Time to Purge the Filth',
    image: '/tutorial/image14.png',
  },
];

// ─────────────────────────────────────────────
// FEATURE REFERENCE SECTIONS
// ─────────────────────────────────────────────
const FEATURE_SECTIONS = [
  {
    icon: '🧹',
    title: 'Wallet Cleanup',
    description: 'The main panel. Browse all toxic objects in your wallet — spam tokens, scam airdrops, and dust. Each object shows its USD value. Select what you want gone, then hit Purge.',
    tips: [
      'Select All purges everything in one shot',
      'Objects are grouped by type for easy scanning',
      'Red DUST badge = zero-value token safe to delete',
      '$85+ objects are auto-protected by default',
    ],
  },
  {
    icon: '⚗️',
    title: 'The Distillery',
    description: 'The purification engine. Watch your selected objects get transformed into $DISTIL rewards in real time. The split is always 92% to you, 7% protocol, 1% burned forever.',
    tips: [
      'Holding bonuses apply automatically (Bronze/Silver/Gold/Diamond)',
      'Toilet Mode adds +15% on top of your holding bonus',
      'Daily limits: 20 purges/day free · 50/day with Toilet Mode',
      'Storage rebates from Sui network are included in your payout',
    ],
  },
  {
    icon: '🏆',
    title: 'Badge & Tier System',
    description: 'Earn purity points with every purge and climb through 8 legendary tiers: Novice → Apprentice → Purifier → Alchemist → Sage → Guardian → Champion → Mythic.',
    tips: [
      'Your badge is displayed on the leaderboard',
      'Higher tiers unlock visual upgrades and flex status',
      'Points never reset — every purge counts forever',
    ],
  },
  {
    icon: '💎',
    title: 'Holding Bonuses',
    description: 'Hold $DISTIL and earn bonus rewards on every purge — no staking, no locking. Just hold.',
    tips: [
      '🥉 Bronze: Hold 1K+ → +5% bonus',
      '🥈 Silver: Hold 10K+ → +10% bonus',
      '🏆 Gold: Hold 100K+ → +15% bonus',
      '💎 Diamond: Hold 1M+ → +20% + revenue share',
    ],
  },
  {
    icon: '🛡️',
    title: 'Whitelist',
    description: 'Protect objects you never want purged. Click the ⋮ menu on any wallet object and select Add to Whitelist. Whitelisted objects are completely invisible to the purge engine.',
    tips: [
      'Import a whitelist JSON for bulk protection',
      'Whitelist syncs across sessions',
      'Check your whitelist anytime via the Whitelist button',
    ],
  },
  {
    icon: '🚽',
    title: 'Toilet Mode',
    description: 'Premium tier unlocked by holding 287,000+ $TOILET tokens (~$10). Changes the entire visual aesthetic and unlocks exclusive features.',
    tips: [
      '+15% bonus $DISTIL on every purge',
      '25% gas discount',
      'SpinWheel: spin daily for up to +100 $DISTIL',
      'Flush Frenzy: rapid-fire bonus game',
      'Golden warrior aesthetic and Toilet Mode badge',
      'Toggle between Gold and Dark Cosmic visual themes',
    ],
  },
  {
    icon: '📊',
    title: 'Stats & History',
    description: 'Your personal stats panel shows total purity points, purifications completed, and objects cleaned over time. The History tab shows every purge with full details.',
    tips: [
      'History includes timestamps, objects purged, and $DISTIL earned',
      'Stats update in real time after every purge',
      'Protocol-wide stats shown alongside your personal stats',
    ],
  },
  {
    icon: '🏅',
    title: 'Leaderboard',
    description: 'See where you rank against all other Distil Protocol users. Rankings are based on total purity points. Set a custom username to show off your status.',
    tips: [
      'Set your username in the leaderboard panel',
      'Your badge tier is displayed next to your rank',
      'Updated after every purge',
    ],
  },
];

// ─────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────
export default function HelpGuide({ isOpen, onClose }) {
  const [activeTab, setActiveTab] = useState('quickstart');
  const [currentStep, setCurrentStep] = useState(0);

  // Reset to step 0 when opened
  useEffect(() => {
    if (isOpen) {
      setCurrentStep(0);
      setActiveTab('quickstart');
    }
  }, [isOpen]);

  // Keyboard navigation
  const handleKeyDown = useCallback((e) => {
    if (!isOpen) return;
    if (e.key === 'Escape') onClose();
    if (activeTab === 'quickstart') {
      if (e.key === 'ArrowRight' && currentStep < QUICK_START_STEPS.length - 1) setCurrentStep(s => s + 1);
      if (e.key === 'ArrowLeft' && currentStep > 0) setCurrentStep(s => s - 1);
    }
  }, [isOpen, activeTab, currentStep, onClose]);

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  if (!isOpen) return null;

  const step = QUICK_START_STEPS[currentStep];
  const isLastStep = currentStep === QUICK_START_STEPS.length - 1;

  return (
    <div className="hg-backdrop" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="hg-modal">

        {/* ── HEADER ── */}
        <div className="hg-header">
          <div className="hg-header-left">
            <span className="hg-logo">🚽💎</span>
            <div>
              <div className="hg-title">Distil Protocol Help</div>
              <div className="hg-subtitle">Quick Start &amp; Feature Guide</div>
            </div>
          </div>
          <button className="hg-close" onClick={onClose} aria-label="Close help">✕</button>
        </div>

        {/* ── TABS ── */}
        <div className="hg-tabs">
          <button
            className={`hg-tab ${activeTab === 'quickstart' ? 'hg-tab--active' : ''}`}
            onClick={() => setActiveTab('quickstart')}
          >
            🚀 Quick Start
          </button>
          <button
            className={`hg-tab ${activeTab === 'features' ? 'hg-tab--active' : ''}`}
            onClick={() => setActiveTab('features')}
          >
            📖 Feature Guide
          </button>
        </div>

        {/* ── QUICK START TAB ── */}
        {activeTab === 'quickstart' && (
          <div className="hg-body">
            {/* Step dots */}
            <div className="hg-dots">
              {QUICK_START_STEPS.map((_, i) => (
                <button
                  key={i}
                  className={`hg-dot ${i === currentStep ? 'hg-dot--active' : ''} ${i < currentStep ? 'hg-dot--done' : ''}`}
                  onClick={() => setCurrentStep(i)}
                  aria-label={`Step ${i + 1}`}
                />
              ))}
            </div>

            {/* Step counter */}
            <div className="hg-step-counter">Step {currentStep + 1} of {QUICK_START_STEPS.length}</div>

            {/* Screenshot */}
            <div className="hg-screenshot-wrap">
              <img
                key={step.image}
                src={step.image}
                alt={step.title}
                className="hg-screenshot"
              />
            </div>

            {/* Step content */}
            <div className="hg-step-content">
              <h3 className="hg-step-title">{step.title}</h3>
              <p className="hg-step-caption">{step.caption}</p>
            </div>

            {/* Navigation */}
            <div className="hg-nav">
              <button
                className="hg-nav-btn hg-nav-btn--secondary"
                onClick={onClose}
              >
                Skip
              </button>
              <div className="hg-nav-arrows">
                <button
                  className="hg-nav-btn hg-nav-btn--ghost"
                  onClick={() => setCurrentStep(s => s - 1)}
                  disabled={currentStep === 0}
                >
                  ← Back
                </button>
                {isLastStep ? (
                  <button
                    className="hg-nav-btn hg-nav-btn--primary"
                    onClick={onClose}
                  >
                    LFG! 🚀
                  </button>
                ) : (
                  <button
                    className="hg-nav-btn hg-nav-btn--primary"
                    onClick={() => setCurrentStep(s => s + 1)}
                  >
                    Next →
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        {/* ── FEATURE GUIDE TAB ── */}
        {activeTab === 'features' && (
          <div className="hg-body hg-body--features">
            <div className="hg-features-grid">
              {FEATURE_SECTIONS.map((section) => (
                <div key={section.title} className="hg-feature-card">
                  <div className="hg-feature-header">
                    <span className="hg-feature-icon">{section.icon}</span>
                    <h3 className="hg-feature-title">{section.title}</h3>
                  </div>
                  <p className="hg-feature-desc">{section.description}</p>
                  <ul className="hg-feature-tips">
                    {section.tips.map((tip, i) => (
                      <li key={i} className="hg-feature-tip">
                        <span className="hg-tip-bullet">▸</span>
                        {tip}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>

            {/* Footer CTA */}
            <div className="hg-features-footer">
              <span className="hg-built-by">✨ Built with Human + AI collaboration in 2026</span>
              <button className="hg-nav-btn hg-nav-btn--primary" onClick={onClose}>
                Let's Purge! 🚽
              </button>
            </div>
          </div>
        )}

      </div>
    </div>
  );
}
